import { normalizeLanguageCode } from "@babackapp/translations/language-utils";

/**
 * Ordered from strongest to weakest. A source only applies when no stronger one
 * has already been applied, so this order doubles as the rank.
 */
export const LANGUAGE_SOURCES = [
  "manual",
  "url",
  "storefront",
  "order",
  "browser",
] as const;

export type LanguageSource = (typeof LANGUAGE_SOURCES)[number];

export type LanguageCandidate = {
  source: LanguageSource;
  code: string | null | undefined;
};

export type ResolvedLanguage = {
  source: LanguageSource;
  code: string;
};

/**
 * Languages the widget may switch to: the shop's configured list, kept to the
 * ones the translation bundle actually ships. Either list being empty means
 * "no constraint from that side" rather than "nothing allowed".
 */
export function buildAllowedLanguages(
  shopLanguages: readonly string[] | undefined,
  supportedLanguages: readonly string[] | undefined,
): string[] {
  const shop = (shopLanguages ?? []).map(normalizeLanguageCode);
  const supported = (supportedLanguages ?? []).map(normalizeLanguageCode);

  if (shop.length === 0) return supported;
  if (supported.length === 0) return shop;

  return shop.filter((code) => supported.includes(code));
}

/**
 * First candidate the shop can actually serve, falling back to the shop's own
 * first language. Returns the source that won, so the caller can tell a strong
 * pick (an explicit `?lang=`) from a weak one (browser detection).
 */
export function resolveWidgetLanguage(
  candidates: readonly LanguageCandidate[],
  allowed: readonly string[],
): ResolvedLanguage | null {
  for (const { source, code } of candidates) {
    if (!code) continue;
    const normalized = normalizeLanguageCode(code);
    if (allowed.length === 0 || allowed.includes(normalized)) {
      return { source, code: normalized };
    }
  }

  const fallback = allowed[0];
  return fallback ? { source: "browser", code: fallback } : null;
}
